import { useQuery } from "@tanstack/react-query";
import {
  Users,
  CreditCard,
  FileText,
  Briefcase,
  Activity,
  TrendingUp,
  Zap,
  DollarSign,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Badge } from "@/components/ui/badge";
import { getAnalyticsOverview, getSystemHealth } from "@/services/admin-api";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_HIERARCHY } from "@atsready/shared";

export default function Dashboard() {
  const { user } = useAuth();
  const canViewRevenue = user && ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY["ADMIN"];

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ["analytics-overview"],
    queryFn: getAnalyticsOverview,
  });

  const { data: health } = useQuery({
    queryKey: ["system-health"],
    queryFn: getSystemHealth,
  });

  const stats = analytics?.data;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Dashboard"
        description="Overview of admin operations, revenue, and system health"
      />

      {/* Primary Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsLoading ? "..." : stats?.totalUsers.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              +{stats?.newUsersWeek || 0} this week (+{stats?.newUsersToday || 0} today)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Subscriptions</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsLoading ? "..." : stats?.activeSubscriptions.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">{stats?.totalTrialing || 0} trialing</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Resumes</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsLoading ? "..." : stats?.totalResumes.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">Created by users</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsLoading ? "..." : stats?.totalJobs.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">Tracked by users</p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsLoading ? "..." : `${stats?.trialConversionRate || 0}%`}
            </div>
            <p className="text-xs text-muted-foreground">Trial to paid</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">AI Usage Today</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsLoading ? "..." : (stats?.totalAiUsageToday || 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.totalAiUsageMonth || 0} this month
            </p>
          </CardContent>
        </Card>

        {canViewRevenue && (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">MRR</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  ${analyticsLoading ? "..." : stats?.mrrCurrent.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">Monthly recurring revenue</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Revenue This Month</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  ${analyticsLoading ? "..." : stats?.revenueMonth.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">${stats?.revenueToday || 0} today</p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Second Row */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Tier Breakdown */}
        <Card className="col-span-2">
          <CardHeader>
            <CardTitle>Subscription Breakdown</CardTitle>
            <CardDescription>Users by subscription tier</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.tierBreakdown &&
                Object.entries(stats.tierBreakdown).map(([tier, count]) => (
                  <div key={tier} className="flex items-center">
                    <div className="w-24 font-medium">{tier}</div>
                    <div className="flex-1">
                      <div className="h-2 w-full rounded-full bg-muted">
                        <div
                          className="h-2 rounded-full bg-primary"
                          style={{
                            width: `${(count / stats.totalUsers) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                    <div className="w-16 text-right text-sm text-muted-foreground">
                      {count.toLocaleString()}
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* System Health */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              System Health
            </CardTitle>
            <CardDescription>Current service status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Database</span>
                <Badge
                  variant={
                    health?.data.services.database.status === "healthy" ? "success" : "destructive"
                  }
                >
                  {health?.data.services.database.status || "Unknown"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Redis Cache</span>
                <Badge
                  variant={
                    health?.data.services.redis.status === "healthy"
                      ? "success"
                      : health?.data.services.redis.configured
                        ? "warning"
                        : "outline"
                  }
                >
                  {health?.data.services.redis.status || "Unknown"}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Overall</span>
                <Badge variant={health?.data.status === "healthy" ? "success" : "warning"}>
                  {health?.data.status || "Unknown"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats Row */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-600">{stats?.totalFree || 0}</div>
              <p className="text-sm text-muted-foreground">Free Users</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{stats?.totalPro || 0}</div>
              <p className="text-sm text-muted-foreground">Pro Users</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600">
                {stats?.churnRateMonth || 0}%
              </div>
              <p className="text-sm text-muted-foreground">Churn Rate</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
